import { File } from '@/common/types/file'
import { GeneralInformationData, LastReview } from '@/common/types/generalInformation.type'
import { MaintenanceObjectiveSystemData } from '@/common/types/maintenance/MaintenanceObjectiveSystem.type'
import { MaintenanceForwardingsDirectiveEntryKlass } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingsDirectiveEntryKlasses.type'
import {
  MaintenanceForwardingsCheckinEntryStepOptions,
  MaintenanceForwardingsEstablishmentChoiceEntryStepOptions,
} from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingsShowDirectiveDetails.type'

export type MaintenanceObjectiveActionsData = {
  id: string
  display_name: string
  description: string
}

export type MaintenanceObjectivePartsData = {
  id: string
  display_name: string
  system_parts: {
    id: string
    system: MaintenanceObjectiveSystemData
  }[]
}

export enum MaintenanceForwardingEstablishmentChoiceEntryProposalStatus {
  AwaitingEstablishmentResponse = 'awaiting_establishment_response',
  AwaitingClientResponse = 'awaiting_client_response',
}

export enum MaintenanceForwardingDirectiveEntryStatus {
  Pending = 'pending',
  AwaitingManualStart = 'awaiting_manual_start',
  InProgress = 'in_progress',
  AwaitingClientReview = 'awaiting_client_review',
}

export enum MaintenanceForwardingStatus {
  Canceled = 'canceled',
  Completed = 'completed',
  OnGoingEstablishmentChoice = 'ongoing_establishment_choice',
  OnGoingCheckin = 'ongoing_checkin',
  OnGoingDirective = 'ongoing_directive',
  OnGoingFinancialReview = 'ongoing_financial_review',
}

export type MaintenanceForwardingEstablishmentChoiceEntryProposalData = {
  id: string
  status: MaintenanceForwardingEstablishmentChoiceEntryProposalStatus
  process_started_at: string | null
  canceled_at: string | null
  establishment_accepted_at: string | null
  establishment_rejected_at: string | null
  client_accepted_at: string | null
  client_rejected_at: string | null
  establishment: {
    id: string
    general_information: Pick<GeneralInformationData, 'fantasy_name' | 'company_logo'>
  }
  establishment_accepted_by: string | null
  establishment_rejected_by: string | null
  client_accepted_by: string | null
  client_rejected_by: string | null
}

export type MaintenanceForwardingEstablishmentChoiceEntryData = {
  actual_proposal: MaintenanceForwardingEstablishmentChoiceEntryProposalData
  canceled_at: string | null
  completed_at: string | null
  establishment: {
    id: string
    general_information: Pick<GeneralInformationData, 'fantasy_name' | 'company_logo'>
  }
  process_started_at: string
  status: string
}

export type MaintenanceForwardingCheckinEntryData = {
  id: string
  canceled_at: string | null
  completed_at: string | null
  created_at: string
  delivery_code: string
  equipment_usage_declaration: number | null
  observations: string | null
  process_started_at: string | null
  responsible: {
    id: string
    full_name: string
  }
  status: string
}

export type MaintenanceEquipmentPieceData = {
  brand: {
    display_name: string
    id: string
  }
  display_name: string
  global_trade_item_number: string | null
  id: string
  number: string
  provider: string
  images: File[]
}

export type DiagnosticPieceData = {
  description: string
  id: string
  piece: MaintenanceEquipmentPieceData
  quantity: number
  total_value: string
  unit_price: string
  warranty_in_days: number
}

export type DiagnosticActivityData = {
  description: string
  display_name: string
  id: string
  images: File[]
  quantity: number
  technical_professionals: {
    id: string
    display_name: string
  }[]
  total_value: string
  unit_for_quantity: string
  unit_price: string
  warranty_in_days: number
}

export type MaintenanceDirectiveObjectiveData = {
  action: MaintenanceObjectiveActionsData
  diagnostic_activities: DiagnosticActivityData[]
  diagnostic_pieces: DiagnosticPieceData[]
  does_not_need_estimate: boolean
  id: string
  part: MaintenanceObjectivePartsData
  system: MaintenanceObjectiveSystemData
}

export type MaintenanceForwardingDirectiveEntryDiagnosticEntryData = {
  id: string
  canceled_at: string | null
  completed_at: string | null
  created_at: string
  deadline_to_complete_in_days: number | null
  last_review: LastReview
  objectives: MaintenanceDirectiveObjectiveData[]
  requirement_list_summary: {
    description: string
    steps: {
      name: string
      error: string
      passed: boolean
    }[]
  }
  process_started_at: string
  status: MaintenanceForwardingDirectiveEntryStatus
  technical_response: string
  total_value: string
}

export type MaintenanceForwardingDirectiveEntryExecutionEntryData = {
  id: string
  canceled_at: string | null
  completed_at: string | null
  created_at: string
  diagnostic: {
    id: string
    completed_at: string | null
    deadline_to_complete_in_days: number | null
    status: MaintenanceForwardingDirectiveEntryStatus
    technical_response: string
    total_value: string
  }
  last_review: LastReview
  objectives: MaintenanceDirectiveObjectiveData[]
  process_started_at: string
  status: MaintenanceForwardingDirectiveEntryStatus
}

export type MaintenanceForwardingDirectiveEntryMap = {
  [MaintenanceForwardingsDirectiveEntryKlass.DiagnosticEntry]: MaintenanceForwardingDirectiveEntryDiagnosticEntryData
  [MaintenanceForwardingsDirectiveEntryKlass.ExecutionEntry]: MaintenanceForwardingDirectiveEntryExecutionEntryData
  [MaintenanceForwardingsDirectiveEntryKlass.ScrapEntry]: undefined
}

export type MaintenanceForwardingData<
  DirectiveEntry extends MaintenanceForwardingDirectiveEntryMap[MaintenanceForwardingsDirectiveEntryKlass] = never,
> = {
  canceled_at: string | null
  checkin_entry: MaintenanceForwardingCheckinEntryData
  checkin_entry_type: MaintenanceForwardingsCheckinEntryStepOptions
  checkout_entry: null
  completed_at: string | null
  created_at: string
  directive_entry: DirectiveEntry
  establishment_choice_entry: MaintenanceForwardingEstablishmentChoiceEntryData
  establishment_choice_entry_type: MaintenanceForwardingsEstablishmentChoiceEntryStepOptions
  fiscal_assessment_entry: null
  id: string
  status: MaintenanceForwardingStatus
  updated_at: string
  workflow: {
    id: string
  }
}

export type MaintenanceForwardingResponse = {
  data: {
    data: MaintenanceForwardingData
  }
}
